import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, ArrowUpRight, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const PRIMARY = [
  { to: "/", label: "Home" },
  { to: "/services/", label: "Services" },
];

const SOLUTIONS = [
  { to: "/marketplaces/", label: "Marketplaces" },
  { to: "/premium-marketplaces/", label: "Premium Marketplaces" },
  { to: "/quick-commerce/", label: "Quick Commerce" },
  { to: "/services/a-plus-listing-graphics/", label: "A+ Listing Graphics" },
  { to: "/services/ecommerce-video-creation/", label: "Video Creation" },
  { to: "/web-app-development/", label: "Web & App Development" },
  { to: "/crm-solutions/", label: "CRM Solutions" },
  { to: "/manpower-solutions/", label: "Manpower Solutions" },
  { to: "/sales-events/", label: "Sales Event Prep" },
  { to: "/ecommerce-services-usa/", label: "USA: Amazon & Walmart" },
  { to: "/ecommerce-services-uae/", label: "UAE: Amazon.ae & Noon" },
  { to: "/google-extractor/", label: "Google Extractor Tool" },
];

const RESOURCES = [
  { to: "/tools/marketplace-calculators/", label: "Marketplace Calculators" },
  { to: "/tools/listing-score/", label: "Listing Score Tool" },
  { to: "/calculators/", label: "Legacy Calculators" },
  { to: "/blog/", label: "Blog" },
  { to: "/testimonials/", label: "Testimonials" },
  { to: "/faq/", label: "FAQ" },
  { to: "/about/", label: "About Us" },
];

const TAIL = [
  { to: "/contact/", label: "Contact" },
];

const FLAT_NAV = [...PRIMARY, ...SOLUTIONS, ...RESOURCES, ...TAIL];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <header
      data-testid="site-navbar"
      className="sticky top-0 z-40 border-b-2 border-black bg-white/95 backdrop-blur-md"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 md:px-8 md:py-4">
        <Link to="/" data-testid="brand-logo" className="flex shrink-0 items-center gap-2" aria-label="Orina Enterprise — Home">
          <img src="/orina-logo-header.png" alt="Orina Enterprise — E-commerce Marketplace Growth Partner" className="h-14 w-auto md:h-20 lg:h-24" loading="eager" fetchPriority="high" width="300" height="197" />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-5 lg:flex xl:gap-7">
          {PRIMARY.map((l) => (
            <NavLinkItem key={l.to} l={l} />
          ))}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="nav-solutions" className="flex items-center gap-1 text-sm font-bold uppercase tracking-wide text-black/70 hover:text-black focus:outline-none">
                Solutions <ChevronDown size={14} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="border-2 border-black bg-white p-0 shadow-brutal">
              {SOLUTIONS.map((s) => (
                <DropdownMenuItem key={s.to} asChild className="cursor-pointer rounded-none px-4 py-2 text-sm font-bold uppercase tracking-wide focus:bg-[#FFD700]">
                  <Link to={s.to} data-testid={`nav-solution-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>{s.label}</Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="nav-resources" className="flex items-center gap-1 text-sm font-bold uppercase tracking-wide text-black/70 hover:text-black focus:outline-none">
                Resources <ChevronDown size={14} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="border-2 border-black bg-white p-0 shadow-brutal">
              {RESOURCES.map((r) => (
                <DropdownMenuItem key={r.to} asChild className="cursor-pointer rounded-none px-4 py-2 text-sm font-bold uppercase tracking-wide focus:bg-[#FFD700]">
                  <Link to={r.to} data-testid={`nav-resource-${r.label.toLowerCase().replace(/\s+/g, "-")}`}>{r.label}</Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {TAIL.map((l) => (
            <NavLinkItem key={l.to} l={l} />
          ))}
        </nav>

        <div className="hidden lg:block">
          <button
            data-testid="navbar-cta-audit"
            onClick={() => navigate("/contact/?type=audit")}
            className="btn-brutal"
          >
            Free Audit <ArrowUpRight size={16} />
          </button>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen(!open)}
          className="border-2 border-black bg-[#FFD700] p-2 lg:hidden"
          aria-label="Toggle menu"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {open && (
        <div className="max-h-[80vh] overflow-y-auto border-t-2 border-black bg-white lg:hidden" data-testid="mobile-nav">
          <nav className="flex flex-col px-4 py-4">
            {FLAT_NAV.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `border-b border-black/10 py-3 text-base font-bold uppercase tracking-wide ${
                    isActive ? "text-black" : "text-black/70"
                  }`
                }
                end={l.to === "/"}
              >
                {l.label}
              </NavLink>
            ))}
            <button
              onClick={() => {
                setOpen(false);
                navigate("/contact/?type=audit");
              }}
              data-testid="mobile-cta-audit"
              className="btn-brutal mt-4"
            >
              Free Audit <ArrowUpRight size={16} />
            </button>
          </nav>
        </div>
      )}
    </header>
  );
}

function NavLinkItem({ l }) {
  return (
    <NavLink
      to={l.to}
      data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
      className={({ isActive }) =>
        `text-sm font-bold uppercase tracking-wide transition-colors ${
          isActive
            ? "text-black underline decoration-[#FFD700] decoration-4 underline-offset-8"
            : "text-black/70 hover:text-black"
        }`
      }
      end={l.to === "/"}
    >
      {l.label}
    </NavLink>
  );
}
